import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../lib/axios';
import { initPushNotifications } from '../services/pushService';
import i18n from '../i18n';

import { toast } from 'react-hot-toast';

interface User {
    id: string;
    username: string;
    email: string;
    role: string;
    tier?: string;
    businessName?: string;
    regionId?: string;
    isApproved?: boolean;
    isIndependentDoctor?: boolean;
    permissions: any[];
    locale?: string;
    breeder?: any;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (identifier: string, password: string, credential?: string, additionalData?: any, appleCredential?: string) => Promise<User>;
    signup: (username: string, email: string, password: string, additionalData?: any) => Promise<boolean>;
    logout: (silent?: boolean) => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    useEffect(() => {
        if (user) {
            initPushNotifications();
        }
    }, [user]);

    const fetchRolePermissions = async (role: string) => {
        try {
            const { data } = await api.get('/roles/config');
            const roleConfig = data.find((p: any) => p.roleName === role);
            return roleConfig ? roleConfig.config : [];
        } catch (error) {
            console.error('Failed to fetch role permissions:', error);
            return [];
        }
    };

    const checkAuth = async () => {
        try {
            const response = await api.get('/auth/me');
            const dbUser = response.data.user;
            if (dbUser) {
                const permissions = await fetchRolePermissions(dbUser.role);
                setUser({ ...dbUser, permissions });
                // Only apply DB locale if the user has NOT manually picked a language.
                // localStorage 'i18nextLng' is set by QuickLanguageSelector / LanguageSelector
                // and must win over the DB default to survive Google/Apple login redirects.
                const manualLang = localStorage.getItem('i18nextLng');
                if (dbUser.locale && !manualLang && i18n.language !== dbUser.locale) {
                    i18n.changeLanguage(dbUser.locale);
                }
            } else {
                setUser(null);
            }
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (identifier: string, password: string, credential?: string, additionalData?: any, appleCredential?: string) => {
        const payload: any = { identifier, password, ...additionalData };
        if (credential) payload.credential = credential;
        if (appleCredential) payload.appleCredential = appleCredential;
        const response = await api.post('/auth/login', payload);
        const dbUser = response.data.user;
        const permissions = await fetchRolePermissions(dbUser.role);
        const userWithPerms = { ...dbUser, permissions };
        setUser(userWithPerms);
        // Only apply DB locale if the user has NOT manually picked a language.
        // localStorage 'i18nextLng' is set by QuickLanguageSelector / LanguageSelector
        // and must win over the DB default to survive Google/Apple login redirects.
        const manualLang = localStorage.getItem('i18nextLng');
        if (dbUser.locale && !manualLang && i18n.language !== dbUser.locale) {
            i18n.changeLanguage(dbUser.locale);
        }
        return userWithPerms;
    };

    const signup = async (username: string, email: string, password: string, additionalData?: any) => {
        const response = await api.post('/auth/signup', { username, email, password, ...additionalData });
        if (response.data.user) {
            setUser(response.data.user);
            toast.success('Registration Successful!');
            return true;
        } else {
            toast.success('Account created successfully! 📧 Please check your email and verify your account to log in.', { duration: 8000 });
            return false;
        }
    };

    const logout = async (silent?: boolean) => {
        await api.post('/auth/logout');
        setUser(null);
        if (!silent) toast.success('Logout Successful');
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
